import React, { useMemo, useState } from "react";

const API = process.env.REACT_APP_API_URL;

function formatExpiry(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("de-DE", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

export default function FamilyInviteCard({ patientId }) {
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);

  const inviteUrl = useMemo(() => {
    if (invite?.invite_url) return invite.invite_url;
    if (!invite?.token) return "";
    return `${window.location.origin}/family/invite/${invite.token}`;
  }, [invite]);

  async function handleCreateInvite() {
    setLoading(true);
    setErr("");
    setCopied(false);
    try {
      const res = await fetch(`${API}/patients/${patientId}/family-invites`, {
        method: "POST",
        credentials: "include",
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error || data?.message || "Einladungslink konnte nicht erstellt werden.");
      }
      setInvite(data);
    } catch (error) {
      setErr(error?.message || "Einladungslink konnte nicht erstellt werden.");
    } finally {
      setLoading(false);
    }
  }

  async function handleCopy() {
    if (!inviteUrl) return;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
    } catch {
      setErr("Link konnte nicht kopiert werden.");
    }
  }

  return (
    <section className="rounded-2xl border border-black/10 bg-white/80 p-5 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Angehörige einladen</h2>
          <p className="mt-1 text-sm text-gray-600">
            Erstelle einen persönlichen Einladungslink für Familienmitglieder dieses Bewohners.
          </p>
        </div>
        <button
          type="button"
          onClick={handleCreateInvite}
          disabled={loading}
          className="rounded-full bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"
        >
          {loading ? "Erstelle..." : "Einladungslink erstellen"}
        </button>
      </div>

      {err && (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {err}
        </div>
      )}

      {invite && (
        <div className="mt-4 space-y-3">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
              Einladungslink
            </label>
            <div className="flex gap-2">
              <input
                readOnly
                value={inviteUrl}
                className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 outline-none"
              />
              <button
                type="button"
                onClick={handleCopy}
                className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                {copied ? "Kopiert" : "Kopieren"}
              </button>
            </div>
          </div>
          <p className="text-xs text-gray-500">
            Gueltig bis: {formatExpiry(invite.expires_at)}
          </p>
        </div>
      )}
    </section>
  );
}
